import React, {ComponentType, useState} from "react";
import MegaBookingBot from "./index";
import {IBaseProps, IContainerProps, IHandlers, IProps, IServices} from './types';
import {cloneDeep} from "lodash-es";
import {
  BookingBaseType,
  BookingMethods,
  BookingType,
  IBooking,
  ICustomer,
  IDiarySchedule,
  IDiaryService,
  ITableItem,
  PaymentTypes,
  SECTION_ANY_ID,
  StatusCancelled,
  StatusUnconfirmed,
  IServiceItem,
  IBookingTag,
} from "shared-types/index";
/**
 * Gets the interface types as array of strings so we don't need hand code them each time (and reduces chance for mistakes).
 */
import {keys} from 'ts-transformer-keys';
import {hot} from "react-hot-loader";
import ReactUtilsService from "app/services/reactUtils.service";
import CustomerSearchService from "external-services/customer-search-service"
import BookingLookupService from "external-services/booking-lookup-service/index";
import BookingUpdateService from "external-services/booking-update-service/index";
import ScheduleLookupService from 'external-services/schedule-lookup-service/scheduleLookup.service';
import DateUtilsService from 'external-services/date-utils-service';

const NS = 'MegaBookingBotContainer';

const propsKeys: string[] = keys<IProps>();
const basePropsKeys: string[] = keys<IBaseProps>();
const containerPropsKeys: string[] = keys<IContainerProps>();
const handlersKeys: string[] = keys<IHandlers>();
const servicesKeys: string[] = keys<IServices>();

interface ISuccessStatus {
  success: boolean;
  status?: string;
}

const ThemeWrap = function(all: IContainerProps & IServices) {

  /**
    * Sorts 'all' into IServices, IProps and IContainerProps
    */
  const {
    tablesService,
  }: IServices = ReactUtilsService.getObjectByKeys(all, servicesKeys) as IServices;
  const props: IProps = ReactUtilsService.getObjectByKeys(all, propsKeys) as IProps;
  const containerProps: IContainerProps = ReactUtilsService.getObjectByKeys(all, containerPropsKeys) as IContainerProps;

  /**
   * Passes all base props from container to component
   */
  // @ts-ignore
  basePropsKeys.forEach(key => props[key] = containerProps[key]);


  const [bookingsAdded, setBookingsAdded] = useState<boolean>(false);
  const [customerList, setCustomerList] = useState<ICustomer[]>([]);
  const [selectedCustomer, setSelectedCustomer] = useState<string>('');
  const [bookingType, setBookingType] = useState<BookingType>(BookingType.Booking);

  props.customerList = customerList;

  /**
   * Handlers used to modify app state (via a service) from within the module's container.
   * Similar to redux mapDispatchToProps.
   */
  const handlers: IHandlers = {
    cancelAllBookingsForToday: () => {
      ScheduleLookupService.getSchedule(containerProps.filterDate)
        .then(schedule => {
          BookingLookupService.getBySchedule(schedule)
            .then(result => {
              result.bookings.forEach(b => {
                const origBooking = cloneDeep(b);
                b.status = StatusCancelled;
                BookingUpdateService.updateFromAnywhere(b, origBooking);
              });
            });
        });
    },

    handleCustomerSearch: (query => {
      CustomerSearchService.search({
        q: query,
        count: 10
      }, true).then((result) => {
        console.log(NS, 'searched customers', result);
        setCustomerList(result.items);
      }).catch(error => {
        console.log(NS, 'error finding customers', error)
      });
    }),

    handleCustomerSelect: (customerId => {
      console.log(NS, 'customerId', customerId);
      setSelectedCustomer(customerId);
    }),

    handleBookingType: (bookingType: BookingType) => setBookingType(bookingType),

    addBookings: (qty: number, durationMins, tags: IBookingTag[]) => {
      if (bookingsAdded) return;
      setBookingsAdded(true);

      const existingBookings: IBooking[] = [];
      let bookingsQty = qty;

      const customer: ICustomer = customerList.find(c => c._id === selectedCustomer);

      let bookingsCount = 0;
      let nextTimeSlot: Date;

      const addNonTableBooking = (service: IDiaryService, inheritedResolve?: (success: boolean) => void): Promise<boolean> => {

        return new Promise(resolve => {

          const _resolve = inheritedResolve || resolve;
          bookingsCount++;

          const doNext = () => {
            if (bookingsCount < bookingsQty) {
              addNonTableBooking(service, _resolve);
            } else {
              _resolve(true);
            }
          }

          const newBooking: any = { // not IBooking[] because some properties get added after saving (like _id)
            bookingType,
            type: BookingBaseType.booking,
            people: 2,
            customersCovid: [],
            duration: durationMins,
            preferredSectionId: SECTION_ANY_ID,
            status: StatusUnconfirmed,
            method: BookingMethods.internet,
            locked: false,
            paymentDetails: { paymentType: PaymentTypes.NoPayment },
            tags: tags,
            additionalPaymentDetails: [],
            selectedMenuOptions: [],
            serviceId: service._id,
            tables: [],
            time: service.startTime,
            isManualJoin: false,
            bookingLinks: []
          };

          BookingUpdateService.add(newBooking, customer)
            .then(booking => {
              console.log(NS, 'saved successfully', booking);
              doNext();
            })
            .catch(err => {
              console.log(NS, 'failed to save', err, newBooking);
              _resolve(false);
            });
        })
      }

      const generateBookings = (service: IDiaryService, table: ITableItem): Promise<ISuccessStatus> => {

        return new Promise(resolve => {

          bookingsCount++;

          if (bookingsCount <= bookingsQty) {
            if (!nextTimeSlot) {
              nextTimeSlot = service.startTime;
            } else {
              const prevSlotEndTime = DateUtilsService.addMinutesToTime(cloneDeep(nextTimeSlot), durationMins);
              const thisSlotEndTime = DateUtilsService.addMinutesToTime(cloneDeep(prevSlotEndTime), durationMins);
              if (thisSlotEndTime <= service.endTime ) { // make sure it fits in service
                // @todo: take into account blockouts
                nextTimeSlot = prevSlotEndTime;
              } else {
                // needs to move to next table
                nextTimeSlot = null;
                bookingsCount--;
                resolve({success: true, status: "next-table"});
                return;
              }
            }

            const thisSlotEndTime = DateUtilsService.addMinutesToTime(cloneDeep(nextTimeSlot), durationMins);
            const existingBooking = existingBookings.find(b => {
              // const endTime = DateUtilsService.addMinutesToTime(cloneDeep(b.time), durationMins);
              return b.serviceId === service._id && (
                b.time >= nextTimeSlot && b.time < thisSlotEndTime
              );
            });
            if (existingBooking) {
              // treats as if booking was added, but doesn't actually repeat the booking, instead increases the qty
              bookingsQty++;
              console.log(NS, 'skipping booking for ' + nextTimeSlot.toString());
              resolve({success: true, status: "keep-table"});
              // @todo: figure out why it stops at the end of the service
              return;
            }

            const newBooking: any = { // not IBooking[] because some properties get added after saving (like _id)
              bookingType: BookingType.Booking,
              type: BookingBaseType.booking,
              people: table.minCapacity,
              customersCovid: [],
              duration: durationMins,
              preferredSectionId: table.sectionId,
              status: StatusUnconfirmed,
              method: BookingMethods.internet,
              locked: false,
              paymentDetails: { paymentType: PaymentTypes.NoPayment },
              tags: tags,
              additionalPaymentDetails: [],
              selectedMenuOptions: [],
              serviceId: service._id,
              tables: [table],
              time: nextTimeSlot,
              isManualJoin: false,
              bookingLinks: []
            };

            BookingUpdateService.add(newBooking, customer)
              .then(booking => {
                console.log(NS, 'saved successfully', booking);
                resolve({success: true, status: "keep-table"});
              })
              .catch(err => {
                console.log(NS, 'failed to save', err, newBooking);
                resolve({success: false});
              });
          } else {
            console.log(NS, 'total bookings reached', bookingsCount, bookingsQty);
            resolve({success: false});
            // may need to reload to refresh properly
            // window.location.reload();
          }
        });
      }

      const tableList: {service: IDiaryService, table: ITableItem}[] = [];
      let incr = 0;
      let prevServiceId = "";
      const loopThroughTables = () => {
        const {service, table} = tableList[incr];
        if (prevServiceId && prevServiceId !== service._id) {
          // new service, starts the time from beginning
          nextTimeSlot = null;
          prevServiceId = service._id;
        }

        generateBookings(service, table)
          .then(({success, status}) => {
            if (success) {
              if (incr < tableList.length) {
                if (status === 'next-table') {
                  incr++;
                }
                loopThroughTables();
              } else {
                console.log(NS, "finished");
              }
            } else {
              console.log(NS, "failed");
            }
          });
      }

      function gatherExistingBookings(schedule: IDiarySchedule): Promise<IBooking[]> {
        return BookingLookupService.getBySchedule(schedule)
          .then(result => {
            const _bookings: IBooking[] = result.bookings.filter(b => b.status.name !== StatusCancelled.name);
            return _bookings;
          });
      }




      ScheduleLookupService.getSchedule(containerProps.filterDate)
        .then(schedule => {
          console.log(NS, 'schedule', schedule);

          gatherExistingBookings(schedule)
            .then((_bookings: IBooking[]) => {
              existingBookings.push(..._bookings);

              if (bookingType === BookingType.Booking) {

                schedule.services.forEach((service: IDiaryService, i: number) => {
                  const isLastService = i === schedule.services.length -1;
                  service.tablesIdsList.forEach((tId, j) => {
                    const isLastTable = j === service.tablesIdsList.length -1;
                    tablesService.getTableById(tId)
                      .then(table => {
                        tableList.push({table, service});
                        if (isLastService && isLastTable) {
                          loopThroughTables();
                        }
                      });
                  });
                });

              } else {
                addNonTableBooking(schedule.services[0])
                  .then((success) => {
                    if (success) {
                      console.log(NS, "success");
                    } else {
                      console.log(NS, "failed");
                    }
                  });
              }

            });
        });
    }
  }

  return ReactUtilsService.containerWrap(
    <MegaBookingBot {...props} {...handlers} />
  );
}

/**
 * Returns properties needed for rendering react2angular
 */
export const R2AMegaBookingBot: [ComponentType<unknown>, never[]?, string[]?] = [
  hot(module)(ThemeWrap) as ComponentType<unknown>,
  [...containerPropsKeys] as never[],
  servicesKeys
];
